/**
 * Palmiye Gurme — sözleşme / politika bağlantıları pencerede açılır (Tur 14b)
 *
 * Sepet çekmecesi ve sepet sayfasındaki onay kutusunda (.hdt-cart-checkbox) ya da
 * data-pg-modal-link özniteliği taşıyan bağlantılarda /policies/... ve /pages/... adresleri
 * yeni sekme yerine sayfa üstünde açılan pencerede gösterilir. Kullanıcı sepetten ayrılmaz.
 * İçerik ilk açılışta mağazadan okunur ve oturum boyunca önbellekte tutulur.
 * Okunamazsa bağlantı normal şekilde açılır.
 */
(() => {
  if (window.__pgPolicyModal) return;
  window.__pgPolicyModal = true;

  const cache = new Map();
  let dialog = null;
  let lastFocus = null;

  const SELECTOR = '.hdt-cart-checkbox a[href], a[data-pg-modal-link]';
  const isPolicyUrl = (url) => url.origin === location.origin && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(policies|pages)\//i.test(url.pathname);

  function closeLabel() {
    return document.getElementById('PgCartToast')?.dataset.close || '';
  }

  function ensureStyle() {
    if (document.getElementById('pg-policy-modal-css')) return;
    const s = document.createElement('style');
    s.id = 'pg-policy-modal-css';
    s.textContent = `
.pg-policy-modal{width:min(720px,calc(100vw - 24px));max-height:min(86vh,860px);padding:0;border:0;border-radius:16px;background:#fff;color:#2b2522;box-shadow:0 24px 60px rgb(0 0 0 / .25);overflow:hidden}
.pg-policy-modal[open]{display:flex;flex-direction:column}
.pg-policy-modal::backdrop{background:rgb(20 14 12 / .5)}
.pg-policy-modal__head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 12px 12px 20px;border-bottom:1px solid #efe7e0;flex:0 0 auto}
.pg-policy-modal__title{margin:0;font:inherit;font-size:17px;font-weight:700;line-height:1.3;color:#2b2522;text-transform:none;letter-spacing:0}
.pg-policy-modal__close{display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;padding:0;border:0;border-radius:10px;background:transparent;color:#2b2522;cursor:pointer}
.pg-policy-modal__close:hover{background:#f6f1ea}
.pg-policy-modal__body{flex:1 1 auto;min-height:120px;padding:16px 20px 24px;overflow:auto;overscroll-behavior:contain;font-size:14.5px;line-height:1.65;color:#3b322e}
.pg-policy-modal__body h1,.pg-policy-modal__body h2,.pg-policy-modal__body h3{margin:1.2em 0 .5em;font-size:1.05em;line-height:1.35;text-transform:none;letter-spacing:0}
.pg-policy-modal__body p,.pg-policy-modal__body ul,.pg-policy-modal__body ol{margin:0 0 .8em}
.pg-policy-modal__body a{color:var(--pg-brand,#840b00)}
.pg-policy-modal__body.is-loading{display:flex;align-items:center;justify-content:center}
.pg-policy-modal__spin{width:28px;height:28px;border:3px solid #efe7e0;border-top-color:var(--pg-brand,#840b00);border-radius:50%;animation:pgspin .8s linear infinite}
@keyframes pgspin{to{transform:rotate(360deg)}}
@media (max-width:749px){.pg-policy-modal{width:100vw;max-width:100vw;max-height:88vh;margin:auto 0 0;border-radius:16px 16px 0 0}.pg-policy-modal__body{padding-bottom:calc(24px + env(safe-area-inset-bottom,0px))}}
@media (prefers-reduced-motion:reduce){.pg-policy-modal__spin{animation:none}}`;
    document.head.appendChild(s);
  }

  function build() {
    ensureStyle();
    dialog = document.createElement('dialog');
    dialog.className = 'pg-policy-modal';
    dialog.setAttribute('aria-labelledby', 'PgPolicyTitle');
    dialog.innerHTML = `
      <div class="pg-policy-modal__head">
        <h2 class="pg-policy-modal__title" id="PgPolicyTitle"></h2>
        <button type="button" class="pg-policy-modal__close" data-pg-policy-close>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
      </div>
      <div class="pg-policy-modal__body" tabindex="0"></div>`;
    dialog.querySelector('[data-pg-policy-close]').setAttribute('aria-label', closeLabel());
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target.closest('[data-pg-policy-close]')) dialog.close();
    });
    dialog.addEventListener('close', () => { lastFocus?.focus?.({ preventScroll: true }); });
    document.body.appendChild(dialog);
  }

  async function load(url) {
    if (cache.has(url)) return cache.get(url);
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(String(res.status));
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const body = doc.querySelector('.shopify-policy__body, .shopify-policy__container .rte, main .rte, main');
    if (!body) throw new Error('empty');
    body.querySelectorAll('script, style, iframe, form').forEach((n) => n.remove());
    const title = doc.querySelector('.shopify-policy__title, main h1')?.textContent.trim() || '';
    const data = { title, html: body.innerHTML };
    cache.set(url, data);
    return data;
  }

  async function open(link) {
    const url = new URL(link.href, location.href);
    if (!dialog) build();
    lastFocus = link;
    const titleEl = dialog.querySelector('.pg-policy-modal__title');
    const bodyEl = dialog.querySelector('.pg-policy-modal__body');
    titleEl.textContent = link.textContent.trim();
    bodyEl.classList.add('is-loading');
    bodyEl.innerHTML = '<span class="pg-policy-modal__spin" aria-hidden="true"></span>';
    if (!dialog.open) dialog.showModal();
    try {
      const data = await load(url.pathname);
      if (data.title) titleEl.textContent = data.title;
      bodyEl.classList.remove('is-loading');
      bodyEl.innerHTML = data.html;
      bodyEl.scrollTop = 0;
      bodyEl.focus({ preventScroll: true });
    } catch (_) {
      dialog.close();
      window.location.href = url.href;
    }
  }

  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest(SELECTOR);
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    let url;
    try { url = new URL(link.href, location.href); } catch (_) { return; }
    if (!isPolicyUrl(url)) return;
    e.preventDefault();
    e.stopPropagation(); // onay kutusunun etiketine tıklanmış sayılmasın (kutu işaretini değiştirmesin)
    open(link);
  }, true);
})();
